import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { HEX_SIZE } from '@/core/constants/battle.constants';

export class BattleCameraManager {
    constructor(private viewport: Viewport) {
        this.initDefaultBehavior();
    }

    private initDefaultBehavior() {
        this.viewport
            .drag({ mouseButtons: 'left' })
            .pinch()
            .wheel()
            .clamp({
                direction: 'all',
                underflow: 'center' 
            })
            .clampZoom({
                minScale: 0.35,
                maxScale: 1.25
            });

        this.viewport.decelerate({ friction: 0.85 });
    }

    public updateSize(width: number, height: number) {
        this.viewport.screenWidth = width;
        this.viewport.screenHeight = height;
        
        // Після зміни розміру корисно перевірити межі (clamp)
        // this.viewport.dirty = true;
    }

    public initConstraints(bounds: PIXI.Rectangle) {
        this.viewport.worldWidth = bounds.width - HEX_SIZE * 2;
        this.viewport.worldHeight = bounds.height - HEX_SIZE * 2;

        this.viewport.clamp({
            left: bounds.x,
            top: bounds.y,
            right: bounds.x + bounds.width,
            bottom: bounds.y + bounds.height,
            direction: 'all',
            underflow: 'center'
        });

        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;
        this.viewport.moveCenter(centerX, centerY);

        this.viewport.setZoom(1, true);
    }

    public lock() {
        this.viewport.plugins.pause('drag');
        this.viewport.plugins.pause('wheel');
        this.viewport.plugins.pause('pinch');
    }

    public unlock() {
        this.viewport.plugins.resume('drag');
        this.viewport.plugins.resume('wheel');
        this.viewport.plugins.resume('pinch');
    }


    /**
     * Плавне фокусування на центрі всіх об'єктів
     */
    public async focusOnBattleCenter(units: { x: number, y: number }[]) {
        if (units.length === 0) return;

        const avgX = units.reduce((sum, u) => sum + u.x, 0) / units.length;
        const avgY = units.reduce((sum, u) => sum + u.y, 0) / units.length;

        const minX = Math.min(...units.map(u => u.x));
        const maxX = Math.max(...units.map(u => u.x));
        const minY = Math.min(...units.map(u => u.y));
        const maxY = Math.max(...units.map(u => u.y));

        const padding = HEX_SIZE * 4;
        const scaleX = this.viewport.screenWidth / (maxX - minX + padding * 2);
        const scaleY = this.viewport.screenHeight / (maxY - minY + padding * 2);
        const minScale = Math.min(0.6, this.viewport.screenWidth / 1920 * 0.6);
        const targetScale = Math.max(Math.min(scaleX, scaleY, 0.8), minScale);

        return new Promise<void>((resolve) => {
            this.viewport.snap(avgX, avgY, {
                removeOnInterrupt: true,
                time: 1000,
                ease: 'easeInOutExpo',
            });

            this.viewport.animate({
                scale: targetScale,
                time: 1000,
                ease: 'easeInOutExpo',
                callbackOnComplete: () => resolve()
            });
        });
    }

    /**
     * Плавне фокусування на конкретному юніті
     * @param point Координати юніта
     * @param force Якщо true, фокусує навіть якщо юніт у кадрі
     */
    public async focusOnPoint(
        point: { x: number, y: number }, 
        force = false, 
        onlyBringIntoView = true
    ) {
        const padding = 100;
        this.viewport.plugins.pause('snap');
        const visibleBounds = this.viewport.getVisibleBounds();
        const { width, height, x: vx, y: vy } = visibleBounds;

        // Перевіряємо, чи точка в безпечній зоні
        const isSafe = (
            point.x > vx + padding &&
            point.x < vx + width - padding &&
            point.y > vy + padding &&
            point.y < vy + height - padding
        );

        if (isSafe && !force) return;

        // Визначаємо цільові координати центру в'юпорту
        let targetCenterX = vx + width / 2;
        let targetCenterY = vy + height / 2;

        if (onlyBringIntoView) {
            // Розрахунок мінімального зсуву по X
            const newPadding = padding * 2;
            if (point.x < vx + padding) {
                // Точка занадто зліва: посуваємо в'юпорт вліво
                targetCenterX = point.x - newPadding + width / 2;
            } else if (point.x > vx + width - padding) {
                // Точка занадто справа: посуваємо в'юпорт вправо
                targetCenterX = point.x + newPadding - width / 2;
            }

            // Розрахунок мінімального зсуву по Y (з урахуванням твоїх -50 для UI)
            const topBound = vy + padding;
            const bottomBound = vy + height - padding;
            const targetYWithOffset = point.y - 100;

            if (targetYWithOffset < topBound) {
                targetCenterY = targetYWithOffset - newPadding + height / 2;
            } else if (targetYWithOffset > bottomBound) {
                targetCenterY = targetYWithOffset + newPadding - height / 2;
            }
        } else {
            // Стандартне повне центрування
            targetCenterX = point.x;
            targetCenterY = point.y - 50;
        }

        return new Promise<void>((resolve) => {
            this.viewport.animate({
                position: { x: targetCenterX, y: targetCenterY },
                time: 800,
                ease: 'easeInOutCubic',
                removeOnInterrupt: true,
                callbackOnComplete: () => resolve()
            });

        });
    }

    /**
     * Метод для швидкого "стрибка" без анімації (наприклад, при ініціалізації)
     */
    public jumpToPoint(x: number, y: number) {
        this.viewport.moveCenter(x, y);
    }

}